import { isNumberString } from "@corca/core";
import { operatorSymbolsJson, unitCategories, unitsJson } from "@corca/data";
import { type Node as ProsemirrorNode } from "@tiptap/pm/model";
import { find } from "linkifyjs";

import { SYMBOLS_AUTOCOMPLETE_REGEX } from "@/components/suggestions/providers/operatorSymbolSuggestionsProvider";
import { idGenerator } from "@/utils/idGenerator";

import type {
  InsertNode,
  NodeJson,
  OperatorSymbolsJsonItem,
  Unit,
  UnitTypeValue,
} from "@corca/data";

type TextItem =
  | { type: "number"; text: string; unit?: [Unit, UnitTypeValue] }
  | { type: "operatorSymbol"; text: string; props: OperatorSymbolsJsonItem["props"] }
  | { type: "text"; text: string };

function* tokenize(text: string) {
  // Matches numbers (with decimals), words (Unicode), symbols, and whitespace
  const regex = /\d*\.\d+([eE]\d+)?|\d+([eE]\d+)?|\d+\.\d*([eE]\d+)?|\p{L}+|[^\p{L}\d\s]+|\s+/gu;
  let match = regex.exec(text);
  while (match !== null) {
    yield match[0];
    match = regex.exec(text);
  }
}

const unitMap: Record<string, [Unit, UnitTypeValue]> = {};

const flattenUnits = unitsJson.map((u) => [u, unitCategories(u)[0]] as [Unit, UnitTypeValue]);

for (const [unit, ut] of flattenUnits) {
  if (unit.children[0]?.type === "plainText" && unit.children[0].props) {
    const unitText = unit.children[0].props.value;
    unitMap[unitText] = [unit, ut];
  }
}

const symbolsMap = operatorSymbolsJson.reduce<Record<string, OperatorSymbolsJsonItem>>((acc, s) => {
  const symbol = s.props?.inputSymbol ?? s.symbol;

  if (!acc[symbol]) {
    acc[symbol] = s;
  }

  return acc;
}, {});

export function replaceNodes(node: ProsemirrorNode): {
  nodesJson: NodeJson[];
  nodes: ProsemirrorNode | ProsemirrorNode[];
} {
  if (node.type.name !== "text" || !node.text) {
    return { nodesJson: [], nodes: node };
  }

  const nodeText = node.text;
  const link = find(nodeText).find((item) => item.isLink && item.value === nodeText);

  if (link) {
    return { nodesJson: [], nodes: node };
  }

  const texts: TextItem[] = [];

  for (const token of tokenize(nodeText)) {
    if (isNumberString(token)) {
      texts.push({ type: "number", text: token });
    } else if (SYMBOLS_AUTOCOMPLETE_REGEX.test(token)) {
      const symbolText = symbolsMap[token]?.symbol ?? token;
      const props = symbolsMap[token]?.props ?? {};

      texts.push({
        type: "operatorSymbol",
        text: symbolText,
        props,
      });
    } else {
      texts.push({ type: "text", text: token });
    }
  }

  if (!texts.some((t) => t.type === "number" || t.type === "operatorSymbol")) {
    return { nodesJson: [], nodes: node };
  }

  // Associate units with numbers
  for (let i = 0; i < texts.length; i++) {
    const current = texts[i];
    const next = texts[i + 1];
    const prev = texts[i - 1];

    if (current.type === "number" && prev?.type === "text") {
      if (prev.text in unitMap) {
        const [unit, ut] = unitMap[prev.text];
        if (unit.placement === "prefix") {
          current.unit = [unit, ut];
          prev.text = "";
        }
      }
    }

    if (current.type === "number" && !current.unit && next?.type === "text") {
      if (next.text in unitMap) {
        const [unit, ut] = unitMap[next.text];
        if (unit.placement === "postfix") {
          current.unit = [unit, ut];
          next.text = "";
        }
      }
    }
  }

  const mergedTexts = texts
    .reduce((acc, t) => {
      const last = acc.at(-1);
      if (t.type === "text" && last?.type === "text") {
        last.text += t.text;
      } else {
        acc.push(t);
      }
      return acc;
    }, [] as TextItem[])
    .filter((t) => t.text.length > 0);

  const nodesJson: NodeJson[] = [];
  const nodes: ProsemirrorNode[] = [];

  for (const item of mergedTexts) {
    if (item.type === "text") {
      nodes.push(node.type.schema.text(item.text));
    } else {
      const contentNode = itemToNodeJson(item);

      if (contentNode) {
        const last = nodes.at(-1);
        const lastExpression =
          last?.type?.name === "inlineExpression"
            ? nodesJson.findLast((n) => n.id === last.attrs.nodeId)
            : undefined;

        if (lastExpression) {
          lastExpression.children[0].children.push(contentNode);
        } else {
          const nodeId = idGenerator();

          nodesJson.push({
            id: nodeId,
            type: "wrapper",
            props: {},
            children: [{ type: "inline", props: {}, children: [contentNode] }],
          });
          nodes.push(node.type.schema.node("inlineExpression", { nodeId }));
        }
      }
    }
  }

  return { nodesJson, nodes };
}

function itemToNodeJson(item: TextItem): NodeJson | null {
  switch (item.type) {
    case "operatorSymbol":
      return {
        type: "operatorSymbol",
        props: item.props ?? {},
        children: [
          {
            type: "symbol",
            props: {
              value: item.text,
            },
            children: [],
          },
        ],
      };

    case "number": {
      const children: NodeJson[] = [];

      if (item.unit) {
        children.push({
          type: "unit",
          props: {
            unitType: item.unit[1],
            placement: item.unit[0].placement,
          },
          children: [
            {
              type: "inline",
              props: {},
              children: item.unit[0].children.map(toNodeJson),
            },
          ],
        });
      }

      return {
        type: "number",
        props: {
          value: item.text,
        },
        children,
      };
    }

    default:
      return null;
  }
}

function toNodeJson(n: InsertNode): NodeJson {
  return {
    type: n.type,
    props: n.props || {},
    children: n.children?.map(toNodeJson) ?? [],
  } as NodeJson;
}
